import { z } from 'zod';
import { CivilizationSchema } from './player.js';

/**
 * M10.3: civilization founding. The catalogues below are data, not logic: adding a color or an
 * emblem means appending an entry (the web app draws emblems by id, unknown ids fall back).
 * No faction bonuses: identity only.
 */
export const CIV_COLORS = [
  { id: '#e74c3c', label: 'Bíbor' },
  { id: '#e67e22', label: 'Narancs' },
  { id: '#f1c40f', label: 'Arany' },
  { id: '#2ecc71', label: 'Smaragd' },
  { id: '#1abc9c', label: 'Türkiz' },
  { id: '#3498db', label: 'Azúr' },
  { id: '#4a6cf7', label: 'Kobalt' },
  { id: '#9b59b6', label: 'Ametiszt' },
  { id: '#e91e63', label: 'Magenta' },
  { id: '#ff5722', label: 'Láva' },
  { id: '#94a3b8', label: 'Ezüst' },
  { id: '#a3e635', label: 'Lime' },
] as const;

export const CIV_EMBLEMS = [
  { id: 'nova', label: 'Nova' },
  { id: 'orbit', label: 'Pálya' },
  { id: 'crown', label: 'Korona' },
  { id: 'helix', label: 'Hélix' },
  { id: 'shield', label: 'Pajzs' },
  { id: 'comet', label: 'Üstökös' },
  { id: 'eye', label: 'Szem' },
  { id: 'trident', label: 'Szigony' },
  { id: 'phoenix', label: 'Főnix' },
  { id: 'gear', label: 'Fogaskerék' },
  { id: 'leaf', label: 'Levél' },
  { id: 'spiral', label: 'Spirál' },
] as const;

export const CIV_NAME_MIN = 3;
export const CIV_NAME_MAX = 32;

/** Whole words that may not appear in a civilization name (reserved roles, abuse). Extend freely. */
export const CIV_NAME_BLOCKLIST = [
  'admin', 'administrator', 'moderator', 'mod', 'system', 'rendszer', 'support', 'staff', 'npc', 'server', 'szerver',
  'fuck', 'shit', 'nazi', 'hitler', 'kurva', 'geci', 'fasz', 'picsa', 'buzi', 'szar', 'anyad', 'anyád',
];

/** Lower-case, single-spaced form used for uniqueness ("Nova  Rend" == "nova rend"). */
export function civNameKey(name: string): string {
  return name.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();
}

export const CivNameSchema = z.string()
  .transform(s => s.normalize('NFC').trim().replace(/\s+/g, ' '))
  .pipe(z.string()
    .min(CIV_NAME_MIN, `A név legalább ${CIV_NAME_MIN} karakter`)
    .max(CIV_NAME_MAX, `A név legfeljebb ${CIV_NAME_MAX} karakter`)
    .regex(/^[\p{L}\p{N}][\p{L}\p{N} '.-]*$/u, 'A név betűvel vagy számmal kezdődjön, és csak betűt, számot, szóközt, kötőjelet, pontot vagy aposztrófot tartalmazzon')
    .refine(s => (s.match(/\p{L}/gu) ?? []).length >= 2, 'A névben legyen legalább két betű')
    .refine(s => !civNameKey(s).split(/[^\p{L}\p{N}]+/u).some(w => CIV_NAME_BLOCKLIST.includes(w)), 'Ez a név nem használható'));

const colorIds = CIV_COLORS.map(c => c.id) as unknown as [string, ...string[]];
const emblemIds = CIV_EMBLEMS.map(e => e.id) as unknown as [string, ...string[]];

/** Strict: the world is never chosen by the client (always the server's shared world). */
export const FoundCivilizationRequestSchema = z.object({
  name: CivNameSchema,
  color: z.enum(colorIds, { errorMap: () => ({ message: 'Válassz színt a listából' }) }),
  emblem: z.enum(emblemIds, { errorMap: () => ({ message: 'Válassz emblémát a listából' }) }),
  offerId: z.string().uuid(),
  systemId: z.string().uuid(),
  idempotencyKey: z.string().min(8).max(100),
}).strict();
export type FoundCivilizationRequest = z.infer<typeof FoundCivilizationRequestSchema>;

/** What a player sees about an offered starting area — only that system, nothing about others. */
export const FoundingOfferSchema = z.object({
  systemId: z.string().uuid(),
  name: z.string(),
  starType: z.string(),
  /** Coarse location ("északi perem", "központi régió"), not coordinates. */
  region: z.string(),
  habitability: z.enum(['kiváló', 'jó', 'átlagos']),
  metalPerHour: z.number().int(),
  energyPerHour: z.number().int(),
  laneCount: z.number().int(),
  /** Coarse distance to the nearest existing empire (no names, no directions). */
  isolation: z.enum(['távoli', 'közepes', 'közeli']),
});
export type FoundingOffer = z.infer<typeof FoundingOfferSchema>;

export const StartingPackageSchema = z.object({
  metal: z.number(), energy: z.number(), credits: z.number(),
  ships: z.array(z.object({ class: z.string(), count: z.number() })),
  homeIncomePerHour: z.object({ metal: z.number(), energy: z.number() }),
  protectionHours: z.number(),
});
export type StartingPackage = z.infer<typeof StartingPackageSchema>;

export const FoundingStatusSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('founded'), civilization: CivilizationSchema }),
  z.object({
    state: z.literal('choose'),
    worldName: z.string(),
    offerId: z.string().uuid(),
    expiresAt: z.string().datetime(),
    offers: z.array(FoundingOfferSchema).min(1).max(4),
    startingPackage: StartingPackageSchema,
  }),
  z.object({ state: z.literal('galaxy_full'), worldName: z.string(), message: z.string() }),
]);
export type FoundingStatus = z.infer<typeof FoundingStatusSchema>;

export const FoundCivilizationResponseSchema = z.object({ civilization: CivilizationSchema, created: z.boolean() });
export type FoundCivilizationResponse = z.infer<typeof FoundCivilizationResponseSchema>;
